import { useState } from 'react';
import { Button, Card, CardHeader, CardTitle, CardContent } from '../ui';
import { useAuth } from '../../stores/auth.store';
import api from '../../services/api.service';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  id: 'free' | 'monthly' | 'yearly';
  name: string;
  price: string;
  period: string;
  features: PlanFeature[];
  popular?: boolean;
  savings?: string;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    features: [
      { text: 'Access to 5 classic games', included: true },
      { text: 'Local high scores', included: true },
      { text: 'Basic themes', included: true },
      { text: 'All premium games', included: false },
      { text: 'Global leaderboards', included: false },
      { text: 'Custom themes', included: false },
      { text: 'Ad-free experience', included: false },
    ],
  },
  {
    id: 'monthly',
    name: 'Premium Monthly',
    price: '$4.99',
    period: '/month',
    popular: true,
    features: [
      { text: 'Access to ALL games', included: true },
      { text: 'Global high scores', included: true },
      { text: 'All themes unlocked', included: true },
      { text: 'Exclusive premium games', included: true },
      { text: 'Global leaderboards', included: true },
      { text: 'Custom themes', included: true },
      { text: 'Ad-free experience', included: true },
    ],
  },
  {
    id: 'yearly',
    name: 'Premium Yearly',
    price: '$39.99',
    period: '/year',
    savings: 'Save 33%',
    features: [
      { text: 'Everything in Monthly', included: true },
      { text: 'Best value!', included: true },
      { text: '4 months FREE', included: true },
      { text: 'Priority support', included: true },
      { text: 'Early access to new games', included: true },
      { text: 'Exclusive yearly badge', included: true },
      { text: 'Ad-free experience', included: true },
    ],
  },
];

export function SubscriptionPlans() {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentTier = user?.tier || 'free';

  const handleSubscribe = async (planId: 'monthly' | 'yearly') => {
    if (!isAuthenticated) {
      window.location.href = '/login?redirect=/subscribe';
      return;
    }

    setLoading(planId);
    setError(null);

    try {
      const response = await api.post<{ url?: string; sessionId?: string }>(
        '/subscriptions/create-checkout-session',
        {
          plan: planId,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscribe`,
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-white mb-4">Choose Your Plan</h1>
        <p className="text-slate-400 text-lg">
          Unlock all games and features with Premium
        </p>
      </div>

      {error && (
        <div className="mb-8 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-center">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {plans.map((plan) => (
          <Card
            key={plan.id}
            className={`relative ${
              plan.popular
                ? 'border-purple-500 ring-2 ring-purple-500/50'
                : ''
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-purple-500 text-white text-sm font-semibold px-4 py-1 rounded-full">
                  Most Popular
                </span>
              </div>
            )}

            {plan.savings && (
              <div className="absolute -top-3 right-4">
                <span className="bg-green-500 text-white text-sm font-semibold px-3 py-1 rounded-full">
                  {plan.savings}
                </span>
              </div>
            )}

            <CardHeader className="text-center pt-8">
              <CardTitle className="text-2xl">{plan.name}</CardTitle>
              <div className="mt-4">
                <span className="text-4xl font-bold text-white">{plan.price}</span>
                <span className="text-slate-400">{plan.period}</span>
              </div>
            </CardHeader>

            <CardContent className="pt-6">
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li
                    key={index}
                    className={`flex items-center ${
                      feature.included ? 'text-slate-300' : 'text-slate-500 line-through'
                    }`}
                  >
                    <span className="mr-2">
                      {feature.included ? '✅' : '❌'}
                    </span>
                    {feature.text}
                  </li>
                ))}
              </ul>

              {plan.id === 'free' ? (
                <Button
                  variant="outline"
                  fullWidth
                  disabled={currentTier === 'free'}
                >
                  {currentTier === 'free' ? 'Current Plan' : 'Downgrade'}
                </Button>
              ) : (
                <Button
                  variant={plan.popular ? 'primary' : 'secondary'}
                  fullWidth
                  onClick={() => { if (plan.id !== 'free') handleSubscribe(plan.id); }}
                  disabled={loading !== null || currentTier === 'premium'}
                >
                  {loading === plan.id ? (
                    <span className="flex items-center justify-center">
                      <span className="animate-spin mr-2">⏳</span>
                      Processing...
                    </span>
                  ) : currentTier === 'premium' ? (
                    'Current Plan'
                  ) : (
                    'Subscribe Now'
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-12 text-center text-slate-400 text-sm">
        <p>All subscriptions can be cancelled anytime.</p>
        <p className="mt-2">
          Secure payment processing by{' '}
          <a
            href="https://stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-400 hover:text-purple-300"
          >
            Stripe
          </a>
        </p>
      </div>
    </div>
  );
}
